import React, { useEffect, useState } from "react";

import GaussianDemo from "./Gaussian/GaussianDemo";
import PhysarumDemo from "./Physarum/PhysarumDemo";
import RenderingDemo from "./Rendering/RenderingDemo";
import SnowDemo from "./Snow/SnowDemo";
import ReconstructionDemo from "./Reconstruction/ReconstructionDemo";

import { debugLog } from "../utils/debug";

import "./GraphicsDemo.css";
import "./styles/Workspace.css";
import "./styles/Panel.css";
import "./styles/Controls.css";
import "./styles/Viewer.css";

const DEFAULT_DEMO = "gaussian";

const DEMOS = {
  gaussian: {
    label: "Gaussian Renderer",
    component: GaussianDemo,
  },
  physarum: {
    label: "Physarum",
    component: PhysarumDemo,
  },
  rendering: {
    label: "Rendering Lab",
    component: RenderingDemo,
  },
  snow: {
    label: "Deformable Snow",
    component: SnowDemo,
  },
  reconstruction: {
    label: "Reconstruction",
    component: ReconstructionDemo,
  },
};

const DEMO_ROUTES = new Set(Object.keys(DEMOS));

function readGraphicsRoute() {
  const hash = window.location.hash.replace(/^#/, "");

  if (!hash.startsWith("graphics/")) {
    return {
      demo: DEFAULT_DEMO,
      section: null,
    };
  }

  const parts = hash.slice("graphics/".length).split("/").filter(Boolean);

  return {
    demo: DEMO_ROUTES.has(parts[0]) ? parts[0] : DEFAULT_DEMO,
    section: parts.slice(1).join("/") || null,
  };
}

function writeGraphicsRoute(demo, section = null) {
  const path = section ? `graphics/${demo}/${section}` : `graphics/${demo}`;

  window.history.pushState(
    {
      graphicsDemo: demo,
      graphicsSection: section,
    },
    "",
    `#${path}`,
  );
}

export const GraphicsDemo = () => {
  const initialRoute = readGraphicsRoute();

  const [activeDemo, setActiveDemo] = useState(initialRoute.demo);
  const [activeSection, setActiveSection] = useState(initialRoute.section);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [demoInfo, setDemoInfo] = useState(null);

  const ActiveDemo = DEMOS[activeDemo].component;

  useEffect(() => {
    function handleNavigation() {
      const route = readGraphicsRoute();

      setActiveDemo(route.demo);
      setActiveSection(route.section);
      setDemoInfo(null);
      setControlsOpen(false);
    }

    window.addEventListener("popstate", handleNavigation);
    window.addEventListener("hashchange", handleNavigation);

    return () => {
      window.removeEventListener("popstate", handleNavigation);
      window.removeEventListener("hashchange", handleNavigation);
    };
  }, []);

  function selectDemo(demo) {
    debugLog("Graphics navigation:", demo);

    setActiveDemo(demo);
    setActiveSection(null);
    setDemoInfo(null);
    setControlsOpen(false);

    writeGraphicsRoute(demo);
  }

  function navigateDemo(section) {
    setActiveSection(section);
    setControlsOpen(false);

    writeGraphicsRoute(activeDemo, section);
  }

  return (
    <section className="reconstruction" id="reconstruction">
      <div className="reconstruction-container">
        <header className="reconstruction-header">
          <span className="reconstruction-label">
            GRAPHICS + 3D VISUALIZATION
          </span>

          <h1>Graphics & Vision Lab</h1>

          <p>
            Interactive experiments in real-time rendering, 3D scene
            representation, GPU programming, and computer vision.
          </p>
        </header>

        <div className="lab-mode-selector">
          {Object.entries(DEMOS).map(([key, demo]) => (
            <ModeButton
              key={key}
              active={activeDemo === key}
              onClick={() => selectDemo(key)}
            >
              {demo.label}
            </ModeButton>
          ))}
        </div>

        <div
          className={
            controlsOpen ? "lab-demo-shell controls-open" : "lab-demo-shell"
          }
        >
          <button
            type="button"
            className="mobile-controls-button"
            onClick={() => setControlsOpen((current) => !current)}
            aria-label={controlsOpen ? "Close controls" : "Open controls"}
            aria-expanded={controlsOpen}
          >
            {controlsOpen ? "✕" : "⚙"}
          </button>

          <button
            type="button"
            className="mobile-controls-backdrop"
            aria-label="Close controls"
            tabIndex={controlsOpen ? 0 : -1}
            onClick={() => setControlsOpen(false)}
          />

          <ActiveDemo
            Panel={Panel}
            Control={Control}
            Slider={Slider}
            Stat={Stat}
            route={activeSection}
            onNavigate={navigateDemo}
            onInfoChange={setDemoInfo}
          />
        </div>

        <DemoExplanation info={demoInfo} />
      </div>
    </section>
  );
};

function ExplanationText({ item }) {
  if (typeof item === "string") {
    return item;
  }

  const { text, links = [] } = item;

  if (!links.length) {
    return text;
  }

  const pattern = new RegExp(
    `(${links
      .map(({ term }) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|")})`,
    "g",
  );

  const linkMap = new Map(links.map((link) => [link.term, link]));

  return text.split(pattern).map((part, index) => {
    const link = linkMap.get(part);

    if (!link) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }

    return (
      <a
        key={`${part}-${index}`}
        href={link.url}
        target="_blank"
        rel="noreferrer"
        className="demo-explanation-inline-link"
      >
        {part}
      </a>
    );
  });
}

const DemoExplanation = ({ info }) => {
  if (!info) {
    return null;
  }

  const {
    eyebrow,
    title,
    description,
    implementation = [],
    pipeline = [],
    references = [],
  } = info;

  return (
    <section className="demo-explanation">
      <div className="demo-explanation-header">
        {eyebrow && <span className="demo-explanation-eyebrow">{eyebrow}</span>}

        {title && <h2>{title}</h2>}

        {description && (
          <p className="demo-explanation-description">{description}</p>
        )}
      </div>

      {implementation.length > 0 && (
        <div className="demo-explanation-section">
          <span className="demo-explanation-label">IMPLEMENTATION</span>

          <ul className="demo-explanation-list">
            {implementation.map((item, index) => (
              <li key={index}>
                <ExplanationText item={item} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {pipeline.length > 0 && (
        <div className="demo-explanation-section">
          <span className="demo-explanation-label">PIPELINE</span>

          <div className="demo-explanation-pipeline">
            {pipeline.map((step, index) => (
              <React.Fragment key={`${step}-${index}`}>
                <span className="demo-explanation-pipeline-step">{step}</span>

                {index < pipeline.length - 1 && (
                  <span
                    className="demo-explanation-pipeline-arrow"
                    aria-hidden="true"
                  >
                    →
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {references.length > 0 && (
        <div className="demo-explanation-section">
          <span className="demo-explanation-label">REFERENCES</span>

          <div className="demo-explanation-references">
            {references.map((reference) => (
              <a
                key={reference.url}
                href={reference.url}
                target="_blank"
                rel="noreferrer"
                className="demo-explanation-reference"
              >
                <strong>{reference.title}</strong>
                {reference.meta && <span>{reference.meta}</span>}
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const Panel = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className={
        open ? "reconstruction-panel open" : "reconstruction-panel collapsed"
      }
    >
      <button
        type="button"
        className="panel-header"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className="panel-label">{title}</span>
        <span className="panel-chevron" aria-hidden="true">
          {open ? "▼" : "▶"}
        </span>
      </button>

      {open && <div className="panel-content">{children}</div>}
    </div>
  );
};

const ModeButton = ({ active = false, onClick, children }) => (
  <button
    type="button"
    className={`lab-mode-button${active ? " active" : ""}`}
    onClick={onClick}
  >
    {children}
  </button>
);

const Control = ({ label, checked, onChange }) => (
  <label className="reconstruction-control">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="control-indicator" />
    <span>{label}</span>
  </label>
);

const Slider = ({ label, value, min, max, step, onChange, display }) => (
  <div className="reconstruction-slider">
    <div className="reconstruction-slider-header">
      <span>{label}</span>
      <strong>{display}</strong>
    </div>

    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  </div>
);

const Stat = ({ label, value }) => (
  <div className="reconstruction-stat">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);
